/* global Word */
import log from "../../../utils/logger";
import { SearchFnParams } from "@/types/document";

/**
 * Splits a given string into an array of substrings (chunks) of a specified maximum length.
 *
 * @param input - The string to be split into chunks.
 * @param chunkSize - The maximum length of each chunk. Defaults to 255 if not specified.
 * @returns An array of string chunks, each with a length up to `chunkSize`.
 */
function splitStringIntoChunks(input: string, chunkSize: number = 255): string[] {
  const chunks: string[] = [];
  for (let i = 0; i < input.length; i += chunkSize) {
    const chunk = input.slice(i, i + chunkSize).trim(); // Word's search does not like leading or trailing whitespaces
    if (!chunk.length) continue; // Searching for empty strings throws an error
    chunks.push(chunk);
  }
  return chunks;
}

/**
 * Utility function to search text in Word documents that overcomes the 255 character
 * search limitation in the desktop version of Word.
 *
 * When text is longer than the max allowed length, it uses a wildcard pattern
 * that keeps the beginning and end of the text while replacing the middle with a wildcard.
 *
 * @param params - Object containing search parameters
 * @param range - The Word.Range object to search within, or null to search the document body
 * @returns A Word.Range object if found, null otherwise
 */
export const splitSearch = async (params: SearchFnParams, range: Word.Range | null): Promise<Word.Range | null> => {
  try {
    const { context, text: inputText, options, paragraph, fallbackToParagraphWhenTextNotFound } = params;

    // Determine the text to search for: use inputText if provided, otherwise use paragraph text
    let text = inputText || paragraph?.text;
    if (!text) {
      // If no text is available, log error and return fallback or null
      log.error("No text provided for search", undefined, params);
      return fallbackToParagraphWhenTextNotFound ? range : null;
    }

    // If the text is short enough, perform a normal search and return the first result
    if (text.length <= 255) {
      log.debug("text is shorter than 255 characters", "splitSearch", { params, length: text.length });
      return (range || context.document.body).search(text, options).getFirstOrNullObject();
    }

    // Split the long text into 255-character chunks
    const chunks = splitStringIntoChunks(text);
    log.debug("split text into chunks", "splitSearch", { params, chunks });

    const rangeCollection: Word.RangeCollection[] = [];
    // For each chunk, perform a search and load the results
    for (const chunk of chunks) {
      const searchResult = (range || context.document.body).search(chunk, {
        ...options,
        matchWholeWord: false, // Allow partial matches for chunked search
      });
      searchResult.load("items");
      rangeCollection.push(searchResult);
    }
    // Synchronize the context to fetch search results
    await context.sync();
    log.debug("loaded ranges of matching chunks", "splitSearch", { params, rangeCollection });

    const ranges: Word.Range[] = [];
    // For each search result, get the first matching range and load its text
    for (const searchRange of rangeCollection) {
      const nextRange = searchRange.getFirstOrNullObject();
      nextRange.load("text");
      ranges.push(nextRange);
    }
    // Synchronize again to fetch the loaded texts
    await context.sync(); // Sync all nextRange objects at once

    // Check if any of the ranges are null or empty, if they are, return null
    if (ranges.some((range) => !range || range.isNullObject)) {
      log.debug("no ranges found", "splitSearch", { params, ranges });
      return fallbackToParagraphWhenTextNotFound ? range : null;
    }

    // Merge the found ranges into a single range
    const fullRange = ranges[0].expandToOrNullObject(ranges[ranges.length - 1]);
    log.debug("merged chunked ranges into a single complete range", "splitSearch", { params, fullRange });

    // Return the merged range, or null if nothing was found
    return fullRange;
  } catch (error) {
    // Log and re-throw any errors that occur
    log.error("Error searching document", "splitSearch", error);
    throw error;
  }
};
